import { List, useListContext, CreateButton, TopToolbar, useRedirect } from 'react-admin';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TextField, InputAdornment, MenuItem, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect, useMemo } from 'react';
import { tbl, btn, LEAD_STATUS, badgeStyle, inputSx, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—';

const Toolbar = () => (
  <TopToolbar>
    <CreateButton label="ליד חדש" sx={btn.create} />
  </TopToolbar>
);

const LeadTable = () => {
  // Server-side search + status filter (backend supports filter.q / filter.status)
  const { data = [], isLoading, filterValues, setFilters } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState(filterValues?.q || '');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  useEffect(() => { setPage(1); }, [data]);
  const pageItems = useMemo(() => {
    return data.slice(startIndex, startIndex + renderCount);
  }, [data, startIndex, renderCount]);

  const applySearch = (value) => {
    setSearch(value);
    setFilters({ ...filterValues, q: value || undefined });
  };
  const applyStatus = (value) => {
    setFilters({ ...filterValues, status: value || undefined });
  };

  if (isLoading) return null;

  return (
    <>
    <Stack className="crm-wrap crm-filter-row" direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        placeholder="חיפוש לפי שם, טלפון או אימייל..." aria-label="חיפוש לידים"
        value={search}
        onChange={e => applySearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
          sx: { ...inputSx, direction: 'rtl' },
        }}
        sx={{ width: 340 }}
      />
      <TextField
        select size="small" label="סטטוס"
        value={filterValues?.status || ''}
        onChange={e => applyStatus(e.target.value)}
        sx={{ width: 170, backgroundColor: '#fff', borderRadius: 2 }}
      >
        <MenuItem value="">הכל</MenuItem>
        {Object.entries(LEAD_STATUS).map(([id, { label }]) =>
          <MenuItem key={id} value={id}>{label}</MenuItem>)}
      </TextField>
    </Stack>
    <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }}>#</TableCell>
            <TableCell>שם</TableCell>
            <TableCell>טלפון</TableCell>
            <TableCell>אימייל</TableCell>
            <TableCell align="center">סטטוס</TableCell>
            <TableCell align="center">תאריך</TableCell>
          </TableRow>
        </TableHead>
        <TableBody ref={containerRef}>
          {pageItems.map((lead, i) => (
            <TableRow key={lead.id} onClick={() => redirect('edit', 'leads', lead.id)}
              {...clickableA11y(() => redirect('edit', 'leads', lead.id), { ariaLabel: `פתיחת ליד מספר ${lead.id}` })}
              sx={tbl.bodyRow(i)}>
              <TableCell>
                <Typography fontWeight={600} color="text.secondary" fontSize={13}>#{lead.id}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600} fontSize={14}>
                  {`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={13} color="text.secondary">{lead.phone || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={13} color="text.secondary">{lead.email || '—'}</Typography>
              </TableCell>
              <TableCell align="center">
                <span style={badgeStyle(LEAD_STATUS, lead.status)}>
                  {(LEAD_STATUS[lead.status] || { label: lead.status }).label}
                </span>
              </TableCell>
              <TableCell align="center">
                <Typography fontSize={13} color="text.secondary">{fmtDate(lead.created_at)}</Typography>
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                {search || filterValues?.status ? 'לא נמצאו תוצאות' : 'אין לידים עדיין'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table></Box>
    </TableContainer>
    <FitPagination page={page} setPage={setPage} total={data.length} perPage={fitCount} />
    </>
  );
};

const LeadList = () => (
  <List sort={{ field: 'id', order: 'DESC' }} perPage={1000} pagination={false} storeKey={false} actions={<Toolbar />} title="לידים" sx={tbl.listSx}>
    <LeadTable />
  </List>
);

export default LeadList;
